import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const menuItems = [
  { path: '/', label: 'Дашборд', icon: '\u{1F4CA}' },
  { path: '/problems', label: 'Проблемы', icon: '\u26A0\uFE0F' },
  { path: '/analytics', label: 'Аналитика', icon: '\u{1F4C8}' },
  { path: '/settings', label: 'Настройки', icon: '\u2699\uFE0F' },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [logoutHover, setLogoutHover] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/manager') || location.pathname.startsWith('/dialogue');
    }
    return location.pathname.startsWith(path);
  };

  const getInitials = (name?: string) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const getRoleLabel = (role?: string) => {
    if (role === 'owner') return 'Владелец';
    if (role === 'supervisor') return 'Руководитель';
    return 'Менеджер';
  };

  return (
    <div style={styles.sidebar}>
      {/* Logo */}
      <div style={styles.logoWrap}>
        <span style={styles.logo}>OKK.AI</span>
      </div>

      {/* Navigation */}
      <nav style={styles.nav}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          const hovered = hoveredItem === item.path;
          return (
            <div
              key={item.path}
              onClick={() => navigate(item.path)}
              onMouseEnter={() => setHoveredItem(item.path)}
              onMouseLeave={() => setHoveredItem(null)}
              style={{
                ...styles.navItem,
                ...(active ? styles.navItemActive : {}),
                ...(!active && hovered ? styles.navItemHover : {}),
              }}
            >
              {active && <div style={styles.activeIndicator} />}
              <span style={{ fontSize: 18, lineHeight: 1 }}>{item.icon}</span>
              <span style={{
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                color: active ? '#e0e7ff' : (hovered ? '#cbd5e1' : '#94a3b8'),
              }}>{item.label}</span>
            </div>
          );
        })}
      </nav>

      {/* User Profile */}
      <div style={styles.userSection}>
        <div style={styles.userCard}>
          <div style={styles.avatar}>
            {getInitials(user?.name)}
          </div>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user?.name || 'Пользователь'}</div>
            <div style={styles.userRole}>{getRoleLabel(user?.role)}</div>
          </div>
        </div>
        <div
          onClick={logout}
          onMouseEnter={() => setLogoutHover(true)}
          onMouseLeave={() => setLogoutHover(false)}
          style={{
            ...styles.logoutBtn,
            ...(logoutHover ? styles.logoutBtnHover : {}),
          }}
        >
          Выйти
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: 260,
    background: '#0f172a',
    color: '#fff',
    padding: '28px 16px 20px',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh',
    position: 'fixed' as const,
    left: 0,
    top: 0,
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  logoWrap: {
    padding: '0 12px',
    marginBottom: 36,
  },
  logo: {
    fontSize: 28,
    fontWeight: 800,
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '-0.5px',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
    flex: 1,
  },
  navItem: {
    padding: '11px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 14,
    color: '#94a3b8',
    transition: 'all 0.15s ease',
    position: 'relative' as const,
  },
  navItemActive: {
    background: 'rgba(99, 102, 241, 0.15)',
    color: '#e0e7ff',
  },
  navItemHover: {
    background: 'rgba(99, 102, 241, 0.08)',
  },
  activeIndicator: {
    position: 'absolute' as const,
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 3,
    height: 20,
    background: '#6366f1',
    borderRadius: '0 3px 3px 0',
  },
  userSection: {
    borderTop: '1px solid rgba(255,255,255,0.08)',
    paddingTop: 16,
  },
  userCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px',
    borderRadius: 10,
    background: 'rgba(255,255,255,0.04)',
    marginBottom: 8,
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    flexShrink: 0,
  },
  userInfo: {
    overflow: 'hidden',
  },
  userName: {
    fontWeight: 600,
    fontSize: 13,
    color: '#e2e8f0',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  userRole: {
    fontSize: 11,
    color: '#64748b',
    marginTop: 2,
  },
  logoutBtn: {
    padding: '10px 16px',
    cursor: 'pointer',
    color: '#64748b',
    fontSize: 13,
    textAlign: 'center' as const,
    borderRadius: 8,
    transition: 'all 0.15s ease',
  },
  logoutBtnHover: {
    background: 'rgba(239, 68, 68, 0.1)',
    color: '#f87171',
  },
};

export default Sidebar;
